import { httpsCallable } from 'firebase/functions';
import { FirebaseFunction } from '../firebase';

export const DEFAULT_EMAIL_DOMAIN_BLOCKED_MESSAGE =
	'Gunakan email pribadi yang aktif. Domain email sementara atau disposable tidak didukung.';
export const DEFAULT_BLOCKED_EMAIL_DOMAINS = [
	'1secmail.com',
	'1secmail.net',
	'1secmail.org',
	'10minutemail.com',
	'10minutemail.net',
	'bccto.me',
	'dispostable.com',
	'dropmail.me',
	'emailfake.com',
	'emailnator.com',
	'emailondeck.com',
	'fakeinbox.com',
	'fakemail.net',
	'getairmail.com',
	'getnada.com',
	'grr.la',
	'guerrillamail.biz',
	'guerrillamail.com',
	'guerrillamail.de',
	'guerrillamail.info',
	'guerrillamailblock.com',
	'harakirimail.com',
	'jetable.org',
	'mail-temporaire.fr',
	'maildrop.cc',
	'mailinator.com',
	'mailnesia.com',
	'mintemail.com',
	'moakt.com',
	'mohmal.com',
	'mytemp.email',
	'nada.email',
	'nada.ltd',
	'nwytg.net',
	'sharklasers.com',
	'spambox.us',
	'temp-mail.io',
	'temp-mail.org',
	'tempail.com',
	'tempmail.com',
	'tempmail.email',
	'tempmail.io',
	'tempmail.ninja',
	'tmpmail.org',
	'throwawaymail.com',
	'trashmail.com',
	'trashmail.de',
	'trashmail.fr',
	'yopmail.com',
	'yopmail.fr',
	'yopmail.net',
];
export const DEFAULT_APP_INFORMATION_ITEMS = [];

const APP_INFORMATION_TYPES = ['card', 'popup', 'ticker'];
const APP_INFORMATION_CONTENT_TYPES = ['text', 'image'];
const APP_INFORMATION_POPUP_TEMPLATES = [
	'announcement',
	'feature-highlight',
	'benefit-spotlight',
	'image-story',
	'full-image',
];
const APP_INFORMATION_POPUP_TEMPLATE_LABELS = {
	announcement: 'Announcement',
	'feature-highlight': 'Feature Highlight',
	'benefit-spotlight': 'Benefit Spotlight',
	'image-story': 'Image Story',
	'full-image': 'Full Image',
};

const normalizeText = (value) => String(value || '').trim();

const normalizeStringList = (values = []) => {
	return Array.from(
		new Set(
			(Array.isArray(values) ? values : [])
				.map((value) => normalizeText(value))
				.filter(Boolean)
		)
	);
};

const normalizeInformationContentType = (informationType, contentType) => {
	if (informationType === 'ticker') return 'text';
	if (APP_INFORMATION_CONTENT_TYPES.includes(contentType)) return contentType;
	return 'text';
};

const normalizePopupTemplate = (informationType, popupTemplate) => {
	if (informationType !== 'popup') return '';
	if (APP_INFORMATION_POPUP_TEMPLATES.includes(popupTemplate)) {
		return popupTemplate;
	}
	return 'announcement';
};

const normalizePopupTemplateName = (informationType, popupTemplate) => {
	if (informationType !== 'popup') return '';
	return APP_INFORMATION_POPUP_TEMPLATE_LABELS[popupTemplate] || 'Announcement';
};

const normalizeDomain = (value) =>
	String(value || '')
		.trim()
		.toLowerCase()
		.replace(/^@+/, '')
		.replace(/\.+$/, '');

export const normalizeBlockedEmailDomains = (values = []) => {
	return Array.from(
		new Set(
			(Array.isArray(values) ? values : []).map(normalizeDomain).filter(Boolean)
		)
	).sort();
};

export const normalizeAppInformationItems = (values = []) => {
	return (Array.isArray(values) ? values : [])
		.map((item, index) => {
			const informationType = APP_INFORMATION_TYPES.includes(
				item?.informationType
			)
				? item.informationType
				: 'card';
			const contentType = normalizeInformationContentType(
				informationType,
				item?.contentType
			);
			const priority = Number.isFinite(Number(item?.priority))
				? Number(item.priority)
				: (index + 1) * 10;

			const popupTemplate = normalizePopupTemplate(
				informationType,
				item?.popupTemplate
			);
			const linkLabel =
				informationType === 'popup'
					? normalizeText(item?.linkLabel || item?.ctaLabel)
					: normalizeText(item?.linkLabel);
			const linkUrl =
				informationType === 'popup'
					? normalizeText(item?.linkUrl || item?.ctaUrl)
					: normalizeText(item?.linkUrl);

			return {
				id: normalizeText(item?.id) || `information-${index + 1}`,
				enabled: item?.enabled !== false,
				title: normalizeText(item?.title),
				informationType,
				contentType,
				content: normalizeText(item?.content),
				description: normalizeText(item?.description),
				imageUrl: normalizeText(item?.imageUrl),
				imageStoragePath: normalizeText(item?.imageStoragePath),
				linkUrl,
				linkLabel,
				popupTemplate,
				popupTemplateName: normalizePopupTemplateName(
					informationType,
					popupTemplate
				),
				couponCode: normalizeText(item?.couponCode),
				placement:
					informationType === 'ticker'
						? []
						: normalizeStringList(item?.placement),
				maintenanceTargets:
					informationType === 'ticker'
						? normalizeStringList(item?.maintenanceTargets)
						: [],
				closable: item?.closable !== false,
				priority,
			};
		})
		.filter((item) => {
			if (item.informationType === 'ticker') {
				return item.title || item.content;
			}
			if (item.informationType === 'card') {
				return item.title || item.description || item.imageUrl || item.linkUrl;
			}
			return item.title || item.content || item.description || item.imageUrl;
		})
		.sort((a, b) => {
			if (a.priority !== b.priority) return a.priority - b.priority;
			return a.title.localeCompare(b.title);
		});
};

export const extractEmailDomain = (email = '') => {
	const normalizedEmail = String(email || '')
		.trim()
		.toLowerCase();
	const atIndex = normalizedEmail.lastIndexOf('@');

	if (atIndex === -1) return '';

	return normalizeDomain(normalizedEmail.slice(atIndex + 1));
};

export const isBlockedEmailDomain = (domain, config = {}) => {
	const normalizedDomain = normalizeDomain(domain);

	if (!normalizedDomain) return false;

	const blockedEmailDomains = normalizeBlockedEmailDomains(
		Array.isArray(config?.blockedEmailDomains)
			? config.blockedEmailDomains
			: DEFAULT_BLOCKED_EMAIL_DOMAINS
	);
	const blockSubdomains = config?.blockSubdomains !== false;

	return blockedEmailDomains.some((blockedDomain) => {
		return (
			normalizedDomain === blockedDomain ||
			(blockSubdomains && normalizedDomain.endsWith(`.${blockedDomain}`))
		);
	});
};

export const isEmailDomainBlocked = (email, config = {}) => {
	return isBlockedEmailDomain(extractEmailDomain(email), config);
};

export const normalizePublicAppConfig = (data = {}) => ({
	blockedEmailDomains: normalizeBlockedEmailDomains(
		Array.isArray(data?.blockedEmailDomains)
			? data.blockedEmailDomains
			: DEFAULT_BLOCKED_EMAIL_DOMAINS
	),
	emailDomainBlockedMessage:
		typeof data?.emailDomainBlockedMessage === 'string' &&
		data.emailDomainBlockedMessage.trim()
			? data.emailDomainBlockedMessage.trim()
			: DEFAULT_EMAIL_DOMAIN_BLOCKED_MESSAGE,
	blockSubdomains: data?.blockSubdomains !== false,
	// Gate verifikasi HP sebelum mulai test (default aktif / fail-closed).
	// Enforcement asli ada di backend; FE pakai flag ini untuk sembunyikan
	// banner/CTA verifikasi saat gate sedang dimatikan.
	phoneVerificationGateEnabled: data?.phoneVerificationGateEnabled !== false,
	appInformationItems: normalizeAppInformationItems(
		Array.isArray(data?.appInformationItems)
			? data.appInformationItems
			: DEFAULT_APP_INFORMATION_ITEMS
	),
});

export const fetchPublicAppConfig = async () => {
	const fn = httpsCallable(FirebaseFunction, 'getPublicAppConfig');
	const result = await fn({});
	return normalizePublicAppConfig(result.data?.data || {});
};
